import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type {
  PromocionVista,
  PromocionCrearInput,
  TipoPromocion,
  ProductoVista,
  PaqueteVista,
  DiasSemana,
} from '@shared/types';
import { NumberField } from '../ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';
import { toLocalDateTimeLocal } from '@renderer/utils/dateLocal';
import { promoEnPalabras } from '@shared/promoEnPalabras';
import { VentanaDisponibilidadFields } from '@renderer/components/forms/VentanaDisponibilidadFields';

interface Props {
  abierto: boolean;
  promocion: PromocionVista | null;
  productos: ProductoVista[];
  paquetes: PaqueteVista[];
  onClose: () => void;
  onSaved: () => void;
}

const DIAS_DEFAULT: DiasSemana = {
  lunes: true,
  martes: true,
  miercoles: true,
  jueves: true,
  viernes: true,
  sabado: true,
  domingo: true,
};

/*
  Sprint 74 — Los tipos, nombrados por lo que hacen.

  Decían "% sobre subtotal elegible", "Regalo (NxM)" y "Precio fijo por unidad elegible". Ninguna de
  esas frases se dice en un restaurante: "subtotal elegible" es el nombre de una variable y "NxM" es
  notación de programador. La dueña no puede elegir entre cuatro opciones que no entiende.
*/
const TIPO_LABEL: Record<TipoPromocion, string> = {
  DESCUENTO_PORCENTAJE: 'Descuento por ciento',
  DESCUENTO_MONTO: 'Descuento de una cantidad fija',
  REGALO: 'Algo de regalo',
  PRECIO_FIJO: 'Precio especial',
  N_POR_PRECIO: 'Varios por un precio (2 x $15)',
};

/** Un ejemplo por tipo: es lo que hace que se pueda elegir sin preguntarle a nadie. */
const TIPO_EJEMPLO: Record<TipoPromocion, string> = {
  DESCUENTO_PORCENTAJE: 'Ejemplo: 20% menos en las espadas.',
  DESCUENTO_MONTO: 'Ejemplo: $50 menos cuando el pedido pase de $500.',
  REGALO: 'Ejemplo: unos frijoles charros gratis con tu compra, o el 2x1.',
  PRECIO_FIJO: 'Ejemplo: la cerveza a $25 los martes.',
  N_POR_PRECIO:
    'Ejemplo: 2 chorizos por $40, o 2 salsas por $50 aunque sean de sabores distintos. ' +
    'Lo que sobre se cobra normal.',
};

function isoLocal(d: string): string {
  if (!d) return '';
  return d.length >= 16 ? d.slice(0, 16) : d;
}

export function FormPromocion({ abierto, promocion, productos, paquetes, onClose, onSaved }: Props) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<TipoPromocion>('DESCUENTO_PORCENTAJE');
  const [valor, setValor] = useState<number>(0);
  const [topeMonto, setTopeMonto] = useState<number>(0);
  const [usarTope, setUsarTope] = useState(false);
  const [productoRegaloId, setProductoRegaloId] = useState<number | undefined>(undefined);
  const [cantidadRegalo, setCantidadRegalo] = useState<number>(1);
  const [cantidadMinima, setCantidadMinima] = useState<number>(1);
  // Sprint 22 — monto mínimo del pedido (subtotal) para que la promo gatille.
  const [montoMinimoPedido, setMontoMinimoPedido] = useState<number>(0);
  // Sprint 22 — solo REGALO. false (default) = aditivo/cortesía. true = sustractivo/cupón.
  const [regaloDescuentaDelTotal, setRegaloDescuentaDelTotal] = useState<boolean>(false);
  const [vigenciaInicio, setVigenciaInicio] = useState<string>('');
  const [vigenciaFin, setVigenciaFin] = useState<string>('');
  const [diasSemana, setDiasSemana] = useState<DiasSemana>(DIAS_DEFAULT);
  const [usarDias, setUsarDias] = useState(false);
  /*
    Sprint 74 — Horario y tamaño del grupo.

    Las horas nacen en null (todo el día): así se comportaba todo antes de este sprint y así siguen
    las promociones ya guardadas. `cantidadPaquete` es el "2" de "2 x $15".
  */
  const [horaInicio, setHoraInicio] = useState<number | null>(null);
  const [horaFin, setHoraFin] = useState<number | null>(null);
  const [cantidadPaquete, setCantidadPaquete] = useState<number>(2);
  const [acumulable, setAcumulable] = useState(false);
  const [activa, setActiva] = useState(true);
  const [prioridad, setPrioridad] = useState<number>(100);
  const [productosElegiblesIds, setProductosElegiblesIds] = useState<number[]>([]);
  const [paquetesElegiblesIds, setPaquetesElegiblesIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /*
    Sprint 74 — "Prioridad" vive aquí adentro.

    Le pedía a la dueña el orden en que se aplican sus descuentos —un dato que el motor decide solo y
    que nadie en un restaurante conoce—. No se borra, porque quien tenga tres promociones encimadas
    puede necesitarlo; se guarda detrás de un "Avanzado" con su default puesto.
  */
  const [verAvanzado, setVerAvanzado] = useState(false);

  useEffect(() => {
    if (promocion) {
      setNombre(promocion.nombre);
      setDescripcion(promocion.descripcion ?? '');
      setTipo(promocion.tipo);
      setValor(promocion.valor ?? 0);
      setTopeMonto(promocion.topeMonto ?? 0);
      setUsarTope(typeof promocion.topeMonto === 'number' && promocion.topeMonto > 0);
      setProductoRegaloId(promocion.productoRegaloId ?? undefined);
      setCantidadRegalo(promocion.cantidadRegalo ?? 1);
      setCantidadMinima(promocion.cantidadMinima);
      setMontoMinimoPedido(promocion.montoMinimoPedido ?? 0);
      setRegaloDescuentaDelTotal(promocion.regaloDescuentaDelTotal);
      setVigenciaInicio(isoLocal(promocion.vigenciaInicio));
      setVigenciaFin(isoLocal(promocion.vigenciaFin));
      if (promocion.diasSemana) {
        setDiasSemana(promocion.diasSemana);
        setUsarDias(true);
      } else {
        setDiasSemana(DIAS_DEFAULT);
        setUsarDias(false);
      }
      setHoraInicio(promocion.horaInicio ?? null);
      setHoraFin(promocion.horaFin ?? null);
      setCantidadPaquete(promocion.cantidadPaquete ?? 2);
      setAcumulable(promocion.acumulable);
      setActiva(promocion.activa);
      setPrioridad(promocion.prioridad);
      setProductosElegiblesIds(promocion.productosElegiblesIds);
      setPaquetesElegiblesIds(promocion.paquetesElegiblesIds);
    } else {
      setNombre('');
      setDescripcion('');
      setTipo('DESCUENTO_PORCENTAJE');
      setValor(0);
      setTopeMonto(0);
      setUsarTope(false);
      setProductoRegaloId(undefined);
      setCantidadRegalo(1);
      setCantidadMinima(1);
      setMontoMinimoPedido(0);
      setRegaloDescuentaDelTotal(false);
      const ahora = new Date();
      /*
        Sprint 74 — Un año, no 30 días.

        Con 30 días la promoción se apagaba sola al mes sin que nadie lo pidiera, y cuando dejara de
        aplicarse la dueña iba a pensar que el sistema se descompuso. Es el veto de MASTER CHEF POS
        sobre los plazos: *"un vencimiento medido con el reloj de quien programó, no de quien lo va a
        cumplir"*. La fecha se sigue mostrando con palabras debajo del campo, para que sea una
        decisión y no un default invisible.
      */
      const enUnAno = new Date(ahora.getTime() + 365 * 86400000);
      setVigenciaInicio(toLocalDateTimeLocal(ahora));
      setVigenciaFin(toLocalDateTimeLocal(enUnAno));
      setDiasSemana(DIAS_DEFAULT);
      setUsarDias(false);
      setHoraInicio(null);
      setHoraFin(null);
      setCantidadPaquete(2);
      setAcumulable(false);
      setActiva(true);
      setPrioridad(100);
      setProductosElegiblesIds([]);
      setPaquetesElegiblesIds([]);
    }
    setError(null);
  }, [promocion, abierto]);

  if (!abierto) return null;

  const toggleProducto = (id: number) => {
    setProductosElegiblesIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const togglePaquete = (id: number) => {
    setPaquetesElegiblesIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return;
    }
    if (!vigenciaInicio || !vigenciaFin) {
      setError('Vigencia inicio y fin son obligatorias');
      return;
    }
    if (productosElegiblesIds.length === 0 && paquetesElegiblesIds.length === 0) {
      setError('Debe seleccionar al menos un producto o paquete elegible');
      return;
    }
    if (tipo === 'REGALO' && (productoRegaloId === undefined || cantidadRegalo <= 0)) {
      setError('REGALO requiere producto de regalo y cantidad > 0');
      return;
    }
    if (
      (tipo === 'DESCUENTO_PORCENTAJE' ||
        tipo === 'DESCUENTO_MONTO' ||
        tipo === 'PRECIO_FIJO' ||
        tipo === 'N_POR_PRECIO') &&
      valor <= 0
    ) {
      setError('El valor debe ser mayor a 0');
      return;
    }
    // Sprint 74 — Un grupo de 1 no es "varios por un precio": sería un precio especial.
    if (tipo === 'N_POR_PRECIO' && cantidadPaquete < 2) {
      setError('Pon cuántas piezas entran en el precio: al menos 2');
      return;
    }

    const data: PromocionCrearInput = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || undefined,
      tipo,
      valor: tipo === 'REGALO' ? undefined : valor,
      topeMonto: usarTope && topeMonto > 0 ? topeMonto : undefined,
      productoRegaloId: tipo === 'REGALO' ? productoRegaloId : undefined,
      cantidadRegalo: tipo === 'REGALO' ? cantidadRegalo : undefined,
      cantidadMinima: cantidadMinima > 0 ? cantidadMinima : 1,
      montoMinimoPedido: montoMinimoPedido > 0 ? montoMinimoPedido : undefined,
      regaloDescuentaDelTotal: tipo === 'REGALO' ? regaloDescuentaDelTotal : false,
      vigenciaInicio: new Date(vigenciaInicio).toISOString(),
      vigenciaFin: new Date(vigenciaFin).toISOString(),
      diasSemana: usarDias ? diasSemana : undefined,
      // Sprint 74 — `null` explícito al quitar el horario: `undefined` dejaría el anterior guardado.
      horaInicio,
      horaFin,
      cantidadPaquete: tipo === 'N_POR_PRECIO' ? cantidadPaquete : undefined,
      acumulable,
      activa,
      prioridad,
      productosElegiblesIds,
      paquetesElegiblesIds,
    };

    setEnviando(true);
    const res = promocion
      ? await window.api.promociones.actualizar(promocion.id, data)
      : await window.api.promociones.crear(data);
    setEnviando(false);
    if (res.ok) onSaved();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const nombresElegibles = [
    ...productos.filter((x) => productosElegiblesIds.includes(x.id)).map((x) => x.nombre),
    ...paquetes.filter((x) => paquetesElegiblesIds.includes(x.id)).map((x) => x.nombre),
  ];
  const resumen = promoEnPalabras({
    tipo,
    valor,
    cantidadMinima,
    montoMinimoPedido,
    topeMonto: usarTope ? topeMonto : null,
    productoRegaloNombre: productos.find((x) => x.id === productoRegaloId)?.nombre ?? null,
    cantidadRegalo,
    regaloDescuentaDelTotal,
    nombresElegibles,
    totalElegibles: productos.length + paquetes.length,
    dias: usarDias ? diasSemana : null,
    horaInicio,
    horaFin,
    cantidadPaquete: tipo === 'N_POR_PRECIO' ? cantidadPaquete : null,
    acumulable,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold">
            {promocion ? 'Editar promoción' : 'Nueva promoción'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="promoNombre">Nombre *</label>
              <input
                id="promoNombre"
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="label" htmlFor="promoTipo">Tipo *</label>
              <select
                id="promoTipo"
                className="input"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoPromocion)}
              >
                {(['DESCUENTO_PORCENTAJE', 'DESCUENTO_MONTO', 'REGALO', 'PRECIO_FIJO'] as TipoPromocion[]).map((t) => (
                  <option key={t} value={t}>{TIPO_LABEL[t]}</option>
                ))}
              </select>
              <p className="help-text">{TIPO_EJEMPLO[tipo]}</p>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="promoDesc">Descripción</label>
            <textarea
              id="promoDesc"
              className="input"
              rows={2}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Texto opcional que aparecerá en el ticket"
            />
          </div>

          {tipo !== 'REGALO' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="label">
                  {tipo === 'DESCUENTO_PORCENTAJE'
                    ? '¿Cuánto por ciento de descuento?'
                    : tipo === 'PRECIO_FIJO'
                      ? 'Precio especial de cada uno'
                      : tipo === 'N_POR_PRECIO'
                        ? `Precio de las ${cantidadPaquete} juntas`
                        : '¿Cuánto dinero se descuenta?'}
                </label>
                <NumberField
                  className="input"
                  min={0}
                  decimals={tipo === 'DESCUENTO_PORCENTAJE' ? 0 : 2}
                  value={tipo === 'DESCUENTO_PORCENTAJE' ? valor : centavosAPesos(valor)}
                  onChange={(n) =>
                    setValor(tipo === 'DESCUENTO_PORCENTAJE' ? n : pesosACentavos(n))
                  }
                />
              </div>
              <div className="flex flex-col">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={usarTope}
                    onChange={(e) => setUsarTope(e.target.checked)}
                  />
                  <span className="text-sm font-medium">Tope máximo de descuento</span>
                </label>
                {usarTope && (
                  <NumberField
                    className="input mt-1"
                    min={0}
                    decimals={2}
                    value={centavosAPesos(topeMonto)}
                    onChange={(n) => setTopeMonto(pesosACentavos(n))}
                  />
                )}
              </div>
            </div>
          )}

          {/*
            Sprint 74 — El "2" de "2 x $15".

            Va pegado al precio y con el ejemplo del cartel a la vista, porque es la pareja del
            importe: el precio es de N piezas juntas, no de una.
          */}
          {tipo === 'N_POR_PRECIO' && (
            <div className="max-w-xs">
              <label className="label">¿Cuántas piezas entran en ese precio?</label>
              <NumberField
                className="input"
                min={2}
                decimals={0}
                value={cantidadPaquete}
                onChange={setCantidadPaquete}
              />
              <p className="help-text">
                El &quot;2&quot; de &quot;2 x $15&quot;. Si el cliente pide 3, dos van en promoción y
                la otra a precio normal.
              </p>
              {/*
                Sprint 133 — La respuesta a "esa no supe cómo hacerle".

                La dueña capturó dos promos y se atoró justo en ésta: *"dos salsas iguales o
                combinadas por $50"*. El motor lo hace desde el Sprint 74 —cuenta juntas todas las
                unidades de los productos marcados— pero **ninguna pantalla lo decía**, así que no
                había forma de saber que marcar las tres salsas era exactamente eso.
              */}
              <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <b>¿Valen combinadas?</b> Sí: marca abajo todos los productos que entren y{' '}
                <b>cuentan juntos</b>. Para <i>&quot;2 salsas por $50, iguales o combinadas&quot;</i>,
                marca tus tres salsas y pon 2 aquí.
              </p>
            </div>
          )}

          {tipo === 'REGALO' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="label">Producto de regalo *</label>
                  <select
                    className="input"
                    value={productoRegaloId ?? ''}
                    onChange={(e) =>
                      setProductoRegaloId(e.target.value ? Number(e.target.value) : undefined)
                    }
                  >
                    <option value="">Selecciona…</option>
                    {productos.map((p) => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Cantidad de regalo</label>
                  <NumberField
                    className="input"
                    min={1}
                    decimals={0}
                    value={cantidadRegalo}
                    onChange={setCantidadRegalo}
                  />
                </div>
              </div>

              {/* Sprint 22 — toggle aditivo vs sustractivo */}
              <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mt-0.5"
                    checked={regaloDescuentaDelTotal}
                    onChange={(e) => setRegaloDescuentaDelTotal(e.target.checked)}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">
                      El regalo descuenta su precio del total
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {regaloDescuentaDelTotal ? (
                        <span className="text-amber-700">
                          🏷 <strong>Modo cupón</strong>: el cliente paga el subtotal MENOS el precio del regalo.
                          Útil si el "regalo" es realmente un descuento disfrazado.
                        </span>
                      ) : (
                        <span className="text-emerald-700">
                          🎁 <strong>Modo cortesía</strong> (recomendado): el cliente paga el subtotal completo
                          y se lleva el regalo SIN costo adicional. El costo lo absorbe el negocio.
                          Es lo que esperan las promos tipo "GRATIS con tu compra".
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="label">¿Cuántos tiene que pedir?</label>
              <NumberField
                className="input"
                min={1}
                decimals={0}
                value={cantidadMinima}
                onChange={setCantidadMinima}
              />
              {/* Sprint 74 — "gatillar" y "elegibles" no se dicen en una cocina. */}
              <p className="help-text">Deja 1 si aplica desde el primero.</p>
            </div>
            <div>
              {/* Sprint 22 — monto mínimo del pedido */}
              <label className="label">¿El pedido tiene que pasar de cierto monto?</label>
              <NumberField
                className="input"
                min={0}
                decimals={2}
                value={centavosAPesos(montoMinimoPedido)}
                onChange={(n) => setMontoMinimoPedido(pesosACentavos(n))}
              />
              <p className="help-text">Ejemplo: $300. Deja 0 si no importa cuánto pida.</p>
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={acumulable}
                  onChange={(e) => setAcumulable(e.target.checked)}
                />
                <span className="text-sm">Se junta con otras promociones</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={activa}
                  onChange={(e) => setActiva(e.target.checked)}
                />
                <span className="text-sm">Activa</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="promoIni">Vigencia desde *</label>
              <input
                id="promoIni"
                type="datetime-local"
                className="input"
                value={vigenciaInicio}
                onChange={(e) => setVigenciaInicio(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="promoFin">Vigencia hasta *</label>
              <input
                id="promoFin"
                type="datetime-local"
                className="input"
                value={vigenciaFin}
                onChange={(e) => setVigenciaFin(e.target.value)}
              />
            </div>
          </div>

          {/*
            Sprint 74 — Días y horas juntos, en el orden del cartel.

            Aquí vivía sólo "Limitar por días de la semana". Faltaban las horas, y los carteles del
            negocio dicen "válido de miércoles a viernes DE 1PM A 5PM": sin horario, la promoción de
            $599 aplicaba a las nueve de la noche. El mismo bloque se usa en los paquetes.
          */}
          <VentanaDisponibilidadFields
            queEs="esta promoción"
            valor={{ diasSemana: usarDias ? diasSemana : null, horaInicio, horaFin }}
            onChange={(v) => {
              setUsarDias(v.diasSemana !== null);
              if (v.diasSemana) setDiasSemana(v.diasSemana);
              setHoraInicio(v.horaInicio);
              setHoraFin(v.horaFin);
            }}
          />

          {/* Sprint 23.1 — Listas elegibles con atajos "Todos/Limpiar" + búsqueda + contador.
              Antes la dueña tenía que tildar 31 productos uno por uno si quería "cualquier compra". */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ListaSeleccion
              titulo="¿Sobre qué productos aplica?"
              items={productos.map((p) => ({ id: p.id, nombre: p.nombre }))}
              seleccionados={productosElegiblesIds}
              onToggle={toggleProducto}
              onSeleccionarTodos={() => setProductosElegiblesIds(productos.map((p) => p.id))}
              onLimpiar={() => setProductosElegiblesIds([])}
              emptyText="No hay productos."
            />
            <ListaSeleccion
              titulo="¿Y sobre qué paquetes?"
              items={paquetes.map((p) => ({ id: p.id, nombre: p.nombre }))}
              seleccionados={paquetesElegiblesIds}
              onToggle={togglePaquete}
              onSeleccionarTodos={() => setPaquetesElegiblesIds(paquetes.map((p) => p.id))}
              onLimpiar={() => setPaquetesElegiblesIds([])}
              emptyText="No hay paquetes."
            />
          </div>

          {/*
            Sprint 74 — Avanzado: lo que el sistema decide solo.

            Se conserva por si alguien tiene tres promociones encimadas y necesita mandar el orden,
            pero fuera del camino: preguntárselo por default a quien atiende mesas era pedirle un
            dato que el motor ya sabe calcular.
          */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={() => setVerAvanzado((v) => !v)}
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              {verAvanzado ? '▾' : '▸'} Avanzado (casi nunca hace falta)
            </button>
            {verAvanzado && (
              <div className="mt-2 max-w-xs">
                <label className="label">Orden de aplicación</label>
                <NumberField
                  className="input"
                  min={0}
                  decimals={0}
                  value={prioridad}
                  onChange={setPrioridad}
                />
                <p className="help-text">
                  Si dos promociones aplican al mismo tiempo, se usa primero la del número más chico.
                  Déjalo como está si no tienes promociones encimadas.
                </p>
              </div>
            )}
          </div>

          {/*
            Sprint 74 — Qué va a pasar, en una frase, ANTES de guardar.

            Éste era el hueco más grande de la pantalla: se llenaban quince campos y no había forma
            de saber qué se había creado hasta que llegara un cliente. Una promoción que no se puede
            leer antes de guardarla se prueba con el dinero del cliente.
          */}
          <ResumenPromo texto={resumen} />

          {error && <div className="error-text">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {promocion ? 'Guardar cambios' : 'Crear promoción'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Sprint 74 — "Así se va a aplicar".
 *
 * Tres estados, y los tres útiles: la frase cuando ya se puede afirmar qué va a pasar, la lista de
 * lo que falta cuando todavía no (con salida, nunca un aviso mudo), y la advertencia de dinero
 * cuando la promoción se puede juntar con otras (DT-070: acumuladas pueden dejar el producto casi
 * en cero).
 */
function ResumenPromo({ texto }: { texto: ReturnType<typeof promoEnPalabras> }) {
  return (
    <div className="space-y-2">
      {texto.frase ? (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
            Así se va a aplicar
          </p>
          <p className="mt-0.5 text-sm text-emerald-950">{texto.frase}</p>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Para poder guardarla
          </p>
          <ul className="mt-0.5 space-y-0.5 text-sm text-slate-700">
            {texto.falta.map((f) => (
              <li key={f}>• {f}</li>
            ))}
          </ul>
        </div>
      )}
      {texto.advertencia && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <b>Ojo:</b> {texto.advertencia}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Sprint 23.1 — Lista con atajos Todos/Limpiar + búsqueda + contador.
// Reemplaza el bloque manual que obligaba a tildar 31 productos uno por uno.
// ============================================================================

function ListaSeleccion({
  titulo,
  items,
  seleccionados,
  onToggle,
  onSeleccionarTodos,
  onLimpiar,
  emptyText,
}: {
  titulo: string;
  items: Array<{ id: number; nombre: string }>;
  seleccionados: number[];
  onToggle: (id: number) => void;
  onSeleccionarTodos: () => void;
  onLimpiar: () => void;
  emptyText: string;
}) {
  const [busqueda, setBusqueda] = useState('');
  const q = busqueda.trim().toLowerCase();
  const filtrados = q ? items.filter((i) => i.nombre.toLowerCase().includes(q)) : items;
  const total = items.length;
  const nSel = seleccionados.length;
  const todosSel = total > 0 && nSel === total;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="label !mb-0">
          {titulo} <span className="text-xs font-normal text-slate-500">({nSel}/{total})</span>
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onSeleccionarTodos}
            className="text-[11px] text-primary hover:underline disabled:text-slate-400 disabled:no-underline"
            disabled={total === 0 || todosSel}
          >
            Todos
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={onLimpiar}
            className="text-[11px] text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline"
            disabled={nSel === 0}
          >
            Limpiar
          </button>
        </div>
      </div>
      {total > 6 && (
        <input
          type="text"
          className="input mb-1 text-xs h-8"
          placeholder="Buscar…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      )}
      <div className="max-h-48 overflow-auto rounded border border-slate-200 p-2">
        {total === 0 ? (
          <div className="text-sm text-slate-400">{emptyText}</div>
        ) : filtrados.length === 0 ? (
          <div className="text-sm text-slate-400">Sin resultados.</div>
        ) : (
          filtrados.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 py-0.5">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={seleccionados.includes(p.id)}
                onChange={() => onToggle(p.id)}
              />
              <span className="text-sm">{p.nombre}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
